import {
    useAppInsightsContext,
    useTrackEvent,
} from '@microsoft/applicationinsights-react-js';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
    Anchor,
    Body1,
    Box,
    Flex,
    Heading1,
    PinInput,
    Stack,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import { TalkativeWidget } from '@phx/talkative';

import { sendRecoveryCodeApi } from '../../api/send-recovery-code.api';
import { verifyRecoveryCodeApi } from '../../api/verify-recovery-code.api';
import { PageLayout } from '../../components';
import {
    ErrorMap,
    type ErrorMessageTypes,
} from '../../components/alerts/errors/error-map';
import { InfoMap } from '../../components/alerts/information/information-map';
import { useContactSupport } from '../../hooks/use-contact-support';
import { routes } from '../../routes';

import type { RecoverPasswordPageState } from './RecoverPasswordPage';

export type RecoverPasswordVerifyCodePageState = {
    email: string;
    token: string;
    flowId: string;
};

const RecoverPasswordVerifyCode = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const appInsights = useAppInsightsContext();
    const { setBackOverrideState } = useNavBar();
    const { enableRecoveryAddress } = useFlags();
    const onContactUsClick = useContactSupport();

    const state = location?.state as RecoverPasswordVerifyCodePageState;
    const [searchParams] = useSearchParams();
    const [token, setToken] = useState<string>(state?.token ?? '');
    const [flowId, setFlowId] = useState<string>(state?.flowId ?? '');
    const [code, setCode] = useState<string>('');

    const [showResendMessage, setShowResendMessage] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessageType, setErrorMessageType] = useState<
        ErrorMessageTypes | undefined
    >();

    const Error = errorMessageType ? ErrorMap[errorMessageType] : null;
    const ResendMessage = showResendMessage
        ? InfoMap['RecoveryCodeResentInfo']
        : null;

    const trackSubmit = useTrackEvent(
        appInsights,
        'RECOVER_PASSWORD_VERIFY_CODE_SUBMIT_CLICK',
        {}
    );
    const trackResend = useTrackEvent(
        appInsights,
        'RECOVER_PASSWORD_VERIFY_CODE_RESEND_CLICK',
        {}
    );
    const trackBackButton = useTrackEvent(
        appInsights,
        'RECOVER_PASSWORD_VERIFY_CODE_BACK_BUTTON_CLICK',
        {}
    );

    const onNavigateBack = useCallback(() => {
        trackBackButton({ message: 'Back button in verify code is clicked' });
        navigate(-1);
    }, [navigate, trackBackButton]);

    useEffect(() => {
        setBackOverrideState({ overrideFn: onNavigateBack });
        return () => setBackOverrideState({ overrideFn: null });
    }, [onNavigateBack, setBackOverrideState]);

    const onSendRecoveryCode = async () => {
        trackResend({ message: 'Resend link is clicked' });
        setShowResendMessage(false);
        setErrorMessageType(undefined);
        setLoading(true);
        const callBackUrl = searchParams.get('callback');
        const {
            success: sendOtpSuccess,
            flowId,
            csrfToken,
            error: sendOtpError,
        } = await sendRecoveryCodeApi(
            state?.email,
            callBackUrl ?? '',
            enableRecoveryAddress
        );
        setLoading(false);
        if (sendOtpSuccess) {
            setFlowId(flowId ?? '');
            setToken(csrfToken ?? '');
            setShowResendMessage(true);
            return;
        }
        setErrorMessageType(sendOtpError);
    };

    const onVerifyClick = async (verificationCode: string) => {
        trackSubmit({
            message: 'Verify button is clicked to verify the one-time password',
        });
        setErrorMessageType(undefined);
        setShowResendMessage(false);
        setLoading(true);
        const {
            success: verifyRecoveryOtpSuccess,
            settingsFlowId,
            error: verifyRecoveryOtpError,
        } = await verifyRecoveryCodeApi(verificationCode, flowId, token);
        if (verifyRecoveryOtpSuccess) {
            const recoverPasswordPageState: RecoverPasswordPageState = {
                settingsFlowId: settingsFlowId ?? '',
                email: state?.email ?? '',
            };
            navigate(
                { pathname: routes.recover, search: window.location.search },
                { state: recoverPasswordPageState }
            );
        }
        setLoading(false);
        setErrorMessageType(verifyRecoveryOtpError);
        setCode('');
    };

    const onRestartRecoverFlow = () => {
        trackSubmit({
            message: 'Restart recovery flow process link is clicked',
        });
        navigate(-1);
    };

    const genericText = t('recoverPasswordVerifyCode.genericEmailText');

    return (
        <PageLayout
            loadingOverlay={loading}
            header={
                errorMessageType && Error ? (
                    <Error
                        onLinkClick={onRestartRecoverFlow}
                        onContactUsClick={onContactUsClick}
                    />
                ) : (
                    showResendMessage && ResendMessage && <ResendMessage />
                )
            }
            heading={
                <Stack gap="sm">
                    <Heading1>
                        {t('recoverPasswordVerifyCode.oneTimeVerificationCode')}
                    </Heading1>
                    <Body1>
                        {t(
                            'recoverPasswordVerifyCode.sixDigitVerificationCode',
                            {
                                email: state?.email ? state.email : genericText,
                            }
                        )}
                    </Body1>
                </Stack>
            }
            footer={
                <Flex justify="center" gap="sm">
                    <TalkativeWidget onClick={onContactUsClick} />
                </Flex>
            }
        >
            <Stack gap="lg">
                <PinInput
                    length={6}
                    size="xl"
                    gap="md"
                    // oxlint-disable-next-line jsx-a11y/no-autofocus -- tracked in docs/oxlint-violations.md
                    autoFocus
                    oneTimeCode
                    placeholder=""
                    value={code}
                    onChange={setCode}
                    onComplete={onVerifyClick}
                />
            </Stack>
            <Stack gap="xs">
                <Body1>{t('recoverPasswordVerifyCode.receiveCode')}</Body1>
                <Box>
                    <Anchor
                        data-testid="icon-text-link"
                        onClick={onSendRecoveryCode}
                    >
                        {t('recoverPasswordVerifyCode.resendCode')}
                    </Anchor>
                </Box>
            </Stack>
        </PageLayout>
    );
};

export default RecoverPasswordVerifyCode;
